import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Dashboard from './components/Dashboard';
import InvoiceDesigner from './components/InvoiceDesigner';
import PartnerSetupWizard from './components/PartnerSetupWizard';
import AdminRetailers from "./components/AdminRetailers";
import Analytics from "./components/Analytics";
import SupportChat from './components/SupportChat';
import Login from './components/Login';
import Register from './components/Register';
import Settings from './components/Settings';
import ProductMappings from './components/ProductMappings';
import Demo from './components/Demo';
import './App.css';

function isDemoRoute() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  return path === '/demo' || params.get('preview') === 'true';
}

// ─── Brand tokens ──────────────────────────────────────────────────────────────
const B = {
  navy:       '#0D2B5E',
  navyLight:  '#1A6BB5',
  orange:     '#6DC234',
  orangeHov:  '#5AAD28',
  bg:         '#F0F4FA',
  sidebar:    '#0A1F44',
  sidebarHov: '#112655',
  text:       '#0D1F3C',
  muted:      '#64748B',
  border:     '#E2E8F0',
  white:      '#FFFFFF',
};

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard',   icon: HomeIcon },
  { key: 'partner',   label: 'Partners',    icon: UsersIcon },
  { key: 'products',  label: 'Products',    icon: PackageIcon },
  { key: 'invoice',   label: 'Invoices',    icon: FileIcon },
  { key: 'analytics', label: 'Analytics',   icon: ChartIcon },
  { key: 'admin',     label: 'Admin',       icon: AdminIcon },
  { key: 'settings',  label: 'Settings',    icon: SettingsIcon },
];

// ─── SVG icon components (inline, no deps) ────────────────────────────────────

function HomeIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}
function UsersIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}
function FileIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}
function ChartIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  );
}
function PackageIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  );
}
function AdminIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}
function SettingsIcon({ size = 18, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}
function LogoutIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const Sidebar = ({ currentPage, setPage, open, setOpen }) => {
  const { user, organization, logout } = useAuth();
  const orgInitial = (organization?.name || user?.firstName || 'J').charAt(0).toUpperCase();

  return (
    <>
    {/* Mobile overlay */}
    {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40, display: 'none' }} className="mob-overlay" />}
    <div style={{
      width: '220px',
      minWidth: '220px',
      background: B.sidebar,
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'sticky',
      top: 0,
      borderRight: '1px solid rgba(255,255,255,0.06)',
      zIndex: 50,
    }} className={`app-sidebar${open ? ' sidebar-open' : ''}`}>
      {/* Logo */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="JayChris EDI" style={{ width: '36px', height: '36px', objectFit: 'contain', flexShrink: 0 }} />
          <div>
            <div style={{ color: 'white', fontWeight: '700', fontSize: '14px', fontFamily: 'Inter, sans-serif', lineHeight: 1.2 }}>JayChris EDI</div>
            {organization?.name && (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', marginTop: '1px', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {organization.name}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
        {NAV_ITEMS.map(item => {
          const active = currentPage === item.key;
          return (
            <button
              key={item.key}
              onClick={() => setPage(item.key)}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '9px 12px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                marginBottom: '2px',
                background: active ? `rgba(232, 121, 47, 0.15)` : 'transparent',
                color: active ? B.orange : 'rgba(255,255,255,0.55)',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontWeight: active ? '600' : '500',
                transition: 'all 0.15s',
                textAlign: 'left',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = B.sidebarHov; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; } }}
            >
              <item.icon size={17} active={active} />
              {item.label}
              {active && <div style={{ marginLeft: 'auto', width: '4px', height: '4px', borderRadius: '50%', background: B.orange }} />}
            </button>
          );
        })}

        {/* Live Demo — accent */}
        <div style={{ margin: '12px 0 4px', height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <button
          onClick={() => setPage('demo')}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '9px 12px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            background: currentPage === 'demo' ? 'rgba(232,121,47,0.2)' : 'transparent',
            color: B.orange,
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            fontWeight: '600',
            textAlign: 'left',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          Live Demo
        </button>
      </nav>

      {/* Mobile close button */}
      <div className="mob-close-wrap" style={{ display: 'none', padding: '8px 10px 0' }}>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', fontFamily: 'Inter, sans-serif' }}>✕ Close</button>
      </div>
      {/* User footer */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '10px 12px', borderRadius: '8px',
          background: 'rgba(255,255,255,0.04)',
        }}>
          <div style={{
            width: '30px', height: '30px', borderRadius: '50%',
            background: `linear-gradient(135deg, ${B.navyLight}, ${B.navy})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '13px', fontWeight: '700', fontFamily: 'Inter, sans-serif',
            flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)',
          }}>
            {orgInitial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: '600', fontFamily: 'Inter, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.firstName} {user?.lastName}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </div>
          </div>
          <button
            title="Sign out"
            onClick={() => { logout(); window.location.reload(); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
          >
            <LogoutIcon size={15} />
          </button>
        </div>
      </div>
    </div>
    <style>{`
      @media (max-width: 768px) {
        .app-sidebar { position: fixed !important; left: -220px; top: 0; bottom: 0; transition: left 0.25s ease; }
        .app-sidebar.sidebar-open { left: 0 !important; }
        .mob-overlay { display: block !important; }
        .mob-close-wrap { display: block !important; }
      }
    `}</style>
    </>
  );
};

// ─── Authenticated App ────────────────────────────────────────────────────────

const AuthenticatedApp = () => {
  const { user, token } = useAuth();
  const [currentPage, setCurrentPage] = useState(isDemoRoute() ? 'demo' : 'dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Full-page demo — no sidebar
  if (currentPage === 'demo') {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '10px 24px',
          background: '#0f172a',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <button
            onClick={() => setCurrentPage('dashboard')}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)',
              padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'Inter, sans-serif',
            }}
          >
            ← Dashboard
          </button>
          <div style={{
            width: '24px', height: '24px', borderRadius: '6px',
            background: `linear-gradient(135deg, ${B.orange}, ${B.orangeHov})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: '900', fontSize: '13px',
          }}>J</div>
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>JayChris EDI · Live Demo</span>
        </div>
        <Demo user={user} token={token} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: B.bg, fontFamily: 'Inter, sans-serif' }}>
      <Sidebar currentPage={currentPage} setPage={(p) => { setCurrentPage(p); setSidebarOpen(false); }} open={sidebarOpen} setOpen={setSidebarOpen} />
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {/* Mobile top bar */}
        <div className="mob-topbar" style={{ display: 'none', alignItems: 'center', gap: '12px', padding: '12px 16px', background: B.sidebar, borderBottom: '1px solid rgba(255,255,255,0.07)', position: 'sticky', top: 0, zIndex: 30 }}>
          <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: '7px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            Menu
          </button>
          <img src="/logo.png" alt="JayChris EDI" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
          <span style={{ color: 'white', fontWeight: '700', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>JayChris EDI</span>
        </div>
        <style>{`.mob-topbar { display: none; } @media (max-width: 768px) { .mob-topbar { display: flex !important; } }`}</style>
        {currentPage === 'dashboard' && <Dashboard setPage={setCurrentPage} />}
        {currentPage === 'invoice'   && <InvoiceDesigner setPage={setCurrentPage} />}
        {currentPage === 'analytics' && <Analytics setPage={setCurrentPage} />}
        {currentPage === 'admin'     && <AdminRetailers setPage={setCurrentPage} />}
        {currentPage === 'partner'   && <PartnerSetupWizard setPage={setCurrentPage} />}
        {currentPage === 'products'  && <ProductMappings setPage={setCurrentPage} />}
        {currentPage === 'settings'  && <Settings setPage={setCurrentPage} />}
      </div>

      <SupportChat />
    </div>
  );
};

// ─── Unauthenticated App ──────────────────────────────────────────────────────

const UnauthenticatedApp = ({ onAuth }) => {
  const [authPage, setAuthPage] = useState('login');

  if (isDemoRoute()) {
    return <Demo user={null} token={null} />;
  }

  if (authPage === 'register') {
    return <Register onSwitchToLogin={() => setAuthPage('login')} onSuccess={onAuth} />;
  }

  return <Login onSwitchToRegister={() => setAuthPage('register')} onSuccess={onAuth} />;
};

// ─── Root ─────────────────────────────────────────────────────────────────────

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const [forceAuth, setForceAuth] = useState(false);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: B.sidebar, flexDirection: 'column', gap: '16px',
      }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px',
          background: `linear-gradient(135deg, ${B.orange}, ${B.orangeHov})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontWeight: '900', fontSize: '22px', fontFamily: 'Inter, sans-serif',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}>J</div>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
      </div>
    );
  }

  if (!isAuthenticated && !forceAuth) {
    return <UnauthenticatedApp onAuth={() => setForceAuth(true)} />;
  }

  return <AuthenticatedApp />;
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
